import React, { useState, useRef } from 'react';
import { Search, BookOpen, TrendingUp, DollarSign, PieChart, Calculator, Filter, ChevronDown, ChevronUp, Eye, EyeOff } from 'lucide-react';

const FinancialDictionary = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedTerm, setExpandedTerm] = useState(null);
  const searchRef = useRef(null);

const terms = [
  {
    id: 1,
    number: "1️⃣",
    title: "אגרות חוב (אג\"ח)",
    category: "investment",
    content: "אגרות חוב הן ניירות ערך המייצגות הלוואה שהמשקיע מעניק למדינה או לחברה בתמורה לריבית קבועה או משתנה. הן נחשבות לאפיק השקעה סולידי יחסית, אך מושפעות משינויים בריבית, סיכוני אשראי של המנפיק, ותנאי השוק.",
    bullTip: "אל תתאהב במילה 'סולידי'. אג\"ח זה לא כספת – במיוחד כשיש עליות ריבית או חשש לאי פירעון.",
    practicalTip: "בעמוד המסלול שלך תוכל לראות את אחוז החשיפה הכללית לאג\"ח – וגם את הפיזור בין ישראל לחו\"ל. זה לא אומר איפה בדיוק אתה מושקע – אבל זה מספרים אמיתיים."
  },
  {
    id: 2,
    number: "2️⃣",
    title: "אינפלציה",
    category: "economics",
    content: "אינפלציה היא קצב עליית המחירים במשק, המוביל לשחיקה בערך הכסף. גם אם החיסכון שלך גדל נומינלית, הוא עלול לאבד מערכו הריאלי אם האינפלציה גבוהה מהתשואה.",
    bullTip: "אל תתרגש מתשואה של 3% אם המדד עלה ב־4%. הרווחת? בעצם הפסדת.",
    practicalTip: "אצלנו בגמלנט AI תוכל למצוא את כל המסלולים במוצרי ההשקעה הריאליים תחת רגולציית משרד האוצר, ולראות מי מהם באמת מביס את האינפלציה."
  },
  {
    id: 3,
    number: "3️⃣",
    title: "איזון אקטוארי",
    category: "pension",
    content: "בקרנות פנסיה, האיזון האקטוארי משקף את היחס בין ההתחייבויות העתידיות של הקרן (תשלומי קצבאות) לנכסים הקיימים. הפרה של האיזון עלולה לגרום לקיצוץ בקצבאות או להעלאת דמי ניהול.",
    bullTip: "פנסיה זו התחייבות ל־40 שנה קדימה. ואם האיזון מופר – מישהו משלם את המחיר. בדרך כלל זה לא הגוף המנהל.",
    practicalTip: "בדוק את מצב האיזון האקטוארי של הקרן שלך באתר הרגולטור או בדוחות השנתיים. בעמוד המסלול שלנו תמצא רק את הרכב ההשקעות – לא את היציבות האקטוארית של הקרן."
  },
  {
    id: 4,
    number: "4️⃣",
    title: "אחוז מניות",
    category: "investment",
    content: "החלק היחסי של התיק המושקע במניות. מסלול מנייתי עשוי להכיל 100% מניות, מסלול כללי עד 50%-60%, ומסלול סולידי לרוב 0%. יותר מניות = יותר סיכון, אבל גם פוטנציאל תשואה גבוה יותר.",
    bullTip: "מסלול 'כללי' יכול להיות כמעט סולידי – או כמעט מנייתי. השם דומה, אבל הסיכון שונה לגמרי.",
    practicalTip: "בעמוד המוצר שלך תוכל לראות בדיוק מהי רמת החשיפה למניות בפועל – באחוזים, לא באמירות שיווקיות."
  },
  {
    id: 5,
    number: "5️⃣",
    title: "דמי ניהול – גמל לעומת פנסיה",
    category: "fees",
    content: "בקופות גמל וקרנות השתלמות התשואה היא הגורם המרכזי, והפערים בין גופים יכולים להגיע לכמה אחוזים בשנה. בפנסיה, לעומת זאת, דמי הניהול (מההפקדה ומהצבירה) הם קריטיים, כי הם משפיעים לאורך עשרות שנים.",
    bullTip: "בעולם של קופות גמל, קרנות השתלמות ופוליסות חיסכון – התשואה היא המלך. אבל בפנסיה? שם דמי הניהול הם הסיפור: נגבים גם מהפקדה, גם מהצבירה – ומצטברים לשנים.",
    practicalTip: "בקרנות השתלמות וגמל – אל תיפול רק על דמי ניהול. חפש קודם מי נותן תשואה עקבית. אבל בקרן פנסיה – תבדוק טוב את האחוזים מכל צד. אצלנו בגמלנט AI תוכל לראות בדיוק כמה שילמת – וכמה נשאר לך בסוף."
  },
  {
    id: 6,
    number: "6️⃣",
    title: "אקטואר",
    category: "pension",
    content: "מומחה שמחשב סיכונים עתידיים בקרנות פנסיה, כמו תוחלת חיים והתחייבויות הקרן. תפקידו לוודא שהקרן תוכל לעמוד בתשלומים לאורך זמן.",
    bullTip: "האקטואר הוא לא המנהל ולא הסוכן – הוא זה שאומר את האמת גם כשהיא לא נעימה.",
    practicalTip: "רוצה לדעת אם הקרן שלך בריאה פיננסית? אל תעצור רק בעמוד ההשקעות – כנס לדוחות האקטואריים של הקרן עצמה. שם האמת מוסתרת בשקט."
  },
  {
    id: 7,
    number: "7️⃣",
    title: "אפיק השקעה",
    category: "investment",
    content: "שילוב של סוגי נכסים בתיק – מניות, אג\"ח, נדל\"ן, מט\"ח, השקעות אלטרנטיביות ועוד. הפיזור בין האפיקים קובע את רמת הסיכון והתשואה הצפויה.",
    bullTip: "אותו שם מסלול – השקעות שונות לגמרי. אל תבחר כותרת, תבדוק מה יש בפנים.",
    practicalTip: "בעמוד המסלול שלך תוכל לראות בדיוק כמה הולך למניות, כמה לאג\"ח, וכמה מושקע בישראל לעומת חו\"ל. זו לא פרשנות – זו השקיפות עצמה."
  },
  {
    id: 8,
    number: "8️⃣",
    title: "אסטרטגיית השקעה",
    category: "investment",
    content: "הגישה של הגוף המנהל – האם הוא משקיע באופן פעיל (אקטיבי), מחקה מדדים (פסיבי), או מתמקד באזורים/סקטורים מסוימים? האסטרטגיה משפיעה על התשואה והסיכון.",
    bullTip: "אסטרטגיה אמיתית זה לא סיסמה – זו יכולת לייצר תשואה לאורך זמן, גם כשהשוק שובר שמאלה.",
    practicalTip: "תסתכל על הפיזור בעמוד המסלול שלך – האם אתה בחשיפה רחבה? לחו\"ל? לאג\"ח? זה נותן לך מושג אמיתי על הסגנון של הגוף המנהל."
  },
  {
    id: 9,
    number: "9️⃣",
    title: "אורך חיסכון",
    category: "planning",
    content: "משך הזמן עד שתצטרך למשוך את הכסף. אופק ארוך מאפשר לקחת יותר סיכון (למשל, יותר מניות), ואילו לקראת פרישה כדאי להפחית תנודתיות.",
    bullTip: "הזמן הוא הנכס הכי חשוב שלך. מי שמשקיע צעיר – קונה זמן בזול. מי שמחכה – משלם עליו ביוקר.",
    practicalTip: "בדוק את רמת הסיכון של המסלול שלך בעמוד המוצר. תן לגיל ולאופק שלך להכתיב את רמת החשיפה – לא לשם של המסלול."
  },
  {
    id: 10,
    number: "🔟",
    title: "ביטוח מנהלים",
    category: "pension",
    content: "מוצר המשלב חיסכון פנסיוני עם כיסויים ביטוחיים (כמו ביטוח חיים או נכות). תוכניות ישנות כוללות לעיתים מקדם קצבה מובטח – יתרון משמעותי שמגן מפני שינויים בתוחלת החיים.",
    bullTip: "אם יש לך ביטוח מנהלים עם מקדם מובטח – אתה מחזיק נכס פיננסי שאין לו תחליף. זה כמו חוזה זהב מול העתיד.",
    practicalTip: "בדוק מה תנאי התכנית שלך, מה החשיפה להשקעות, מה דמי הניהול – וכל זה תוכל לראות בעמוד המוצר שלך בגמלנט AI."
  },
  {
    id: 11,
    number: "1️⃣1️⃣",
    title: "נכסים ריאליים",
    category: "investment",
    content: "נכסים ששומרים על ערכם מול אינפלציה – כמו מניות, נדל\"ן, תשתיות, או אג\"ח צמודות מדד. הם נחשבים להגנה טובה יותר ממזומן בטווח ארוך.",
    bullTip: "כסף ששוכב על מזומן שווה פחות בכל שנה. נכס ריאלי הוא היחיד שבאמת נלחם בשבילך נגד הזמן.",
    practicalTip: "בדוק מה שיעור ההשקעה שלך בנכסים ריאליים – למשל מניות ונדל\"ן – בעמוד המסלול שלך. לא תמיד צריך ניתוח עמוק – מספיק לראות כמה באמת חשוף."
  },
  {
    id: 12,
    number: "1️⃣2️⃣",
title: "פיזור השקעות",
    category: "investment",
    content: "העיקרון של חלוקת ההשקעה בין אפיקים, תחומים ומדינות שונות כדי להפחית סיכון. פיזור טוב משפר את היציבות של התיק לאורך זמן.",
    bullTip: "פיזור זה לא טרנד – זה ההבדל בין משקיע ששרד את 2008, לבין אחד שחזר לבנק עם פיג’מה.",
    practicalTip: "תסתכל בעמוד המסלול שלך על הפיזור בין מניות, אג\"ח, חו\"ל וישראל – ותשאל את עצמך: האם התיק הזה חי או תלוי בשוק אחד?"
  },
  {
    id: 13,
    number: "1️⃣3️⃣",
    title: "בטא",
    category: "investment",
    content: "מדד שמראה את תנודתיות המסלול בהשוואה לשוק. בטא 1 = תנודתיות כמו השוק, מעל 1 = תנודתי יותר, מתחת ל-1 = יציב יותר.",
    bullTip: "בטא גבוהה בלי תשואה? אתה סתם מתנדנד. אם לקחת סיכון – תוודא שהוא בא עם תגמול.",
    practicalTip: "תוכל לראות את מדד הבטא במסלולים מסוימים אצלנו – זו דרך להבין אם אתה במסלול סוער, מתון, או פשוט לא מתגמל."
  },
  {
    id: 14,
    number: "1️⃣4️⃣",
    title: "ביצועי מדד הייחוס",
    category: "investment",
    content: "כל מסלול נמדד מול מדד ייחוס (כמו ת\"א 125 או S&P 500). השאלה היא לא רק כמה הרווחת, אלא האם עקפת את המדד.",
    bullTip: "רווח של 5% זה לא רווח – אם המדד עשה 8%. אל תסתפק ב'ירוק' – תבדוק מה עשו אחרים.",
    practicalTip: "במוצרים מסוימים מוצג מדד הייחוס בעמוד המוצר. תוודא שהמסלול שלך לא רק יציב – אלא גם יעיל מול המדד שהוא נבחן לפיו."
  },
  {
    id: 15,
    number: "1️⃣5️⃣",
    title: "ביטחון פיננסי",
    category: "planning",
content: "היכולת לדעת מה יש לך, איפה זה מושקע, ואיך זה מנוהל. ביטחון אמיתי מגיע משקיפות ושליטה על הכסף שלך.",
    bullTip: "ביטחון זה לא פיקדון – זה שליטה. לדעת איפה הכסף שלך, ולא רק לקוות שהוא במקום טוב.",
    practicalTip: "כל מסלול שאתה מושקע בו מופיע אצלנו עם פיזור השקעות, ביצועים, ודמי ניהול. תבדוק אותם כמו שצריך – ואז תרגיש באמת בטוח."
  },
{
  id: 16,
  number: "1️⃣6️⃣",
  title: "ביטוח חיים בפנסיה",
  category: "pension",
  content: "כיסוי ביטוחי שמבטיח תשלום לשארים במקרה פטירה. גובה הקצבה תלוי בסוג הקרן, המסלול, והכיסוי שנבחר.",
  bullTip: "כיסוי אוטומטי? לא אומר שהוא טוב. אל תחכה ליום רע כדי לגלות שזה לא הספיק.",
  practicalTip: "כנס לדוחות הקרן שלך או בדוק עם הסוכן שלך – אל תסתפק במה שמובנה. לפעמים תוספת קטנה שווה שקט נפשי אמיתי, ולפעמים אתה משלם על ביטוח חיים שאתה בכלל לא צריך – על חשבון החיסכון העתידי שלך."
},
{
  id: 17,
  number: "1️⃣7️⃣",
  title: "קרן ברירת מחדל",
  category: "pension",
  content: "קרנות פנסיה שנבחרו על ידי המדינה בשל דמי ניהול נמוכים. מתאימות למי שלא בחר קרן, ומציעות חלופות זולות – לרוב לבעלי שכר שאינו גבוה במיוחד.",
  bullTip: "דווקא בקרנות פנסיה של ברירת מחדל – מחיר נמוך לא אומר ניהול חלש. להיפך: מדובר בבתי ההשקעות המובילים במשק, כמו מיטב, מור, אלטשולר שחם, אינפיניטי ואחרים. לכן, גם עם דמי ניהול נמוכים, ייתכן שתמצא ניהול מקצועי ותשואות תחרותיות.",
  practicalTip: "אל תזלזל בקרן ברירת מחדל רק בגלל שמחירה נמוך. בדוק את ביצועי הקרן לאורך זמן והשווה מול הקרנות האחרות – ייתכן שתופתע לטובה מהתמורה שמקבלים כאן."
},
  {
    id: 18,
    number: "1️⃣8️⃣",
    title: "בקרת סיכונים",
    category: "investment",
    content: "התהליך שבו הגוף המנהל ממזער סיכונים באמצעות פיזור, ניהול נזילות, תקרות השקעה וניתוח מתמשך של הסיכונים.",
    bullTip: "את הסיכון לא רואים כשהשוק עולה – רק כשכולם בורחים ואתה מגלה שאין דלת.",
    practicalTip: "בעמוד המסלול שלך תראה את חלוקת ההשקעות. ככל שהתיק שלך מרוכז או תנודתי יותר – כך תבין כמה הגוף באמת בודק את עצמו."
  },
  {
    id: 19,
    number: "1️⃣9️⃣",
    title: "ברוטו מול נטו",
    category: "fees",
    content: "תשואה ברוטו היא לפני ניכוי דמי ניהול, נטו היא מה שנשאר לך בפועל. ההבדל יכול להגיע לעשרות ומאות אלפי שקלים לאורך זמן.",
    bullTip: "רווח בלי לבדוק דמי ניהול – זה כמו לקבל משכורת בלי לשים לב כמה מיסים לקחו לך.",
    practicalTip: "אנחנו מראים לך את הביצועים נטו – אחרי כל ההוצאות. כי מה שנכנס לכיס – זה מה שחשוב באמת."
  },
  {
    id: 20,
    number: "2️⃣0️⃣",
    title: "בדיקת מסלול השקעה",
    category: "planning",
    content: "הצעד הראשון של כל חוסך: לוודא שהמסלול מתאים לגיל, לאופק ההשקעה, ולרמת הסיכון שאתה מוכן לקחת.",
    bullTip: "מסלול 'כללי' זה רק שם. תבדוק מה יש בפנים – או שתופתע בפעם הבאה שהשוק ייפול.",
    practicalTip: "כנס לדף המסלול שלך – תראה באחוזים מה באמת יש בפנים: מניות, אג\"ח, חו\"ל, ישראל. זה הסיפור האמיתי של הכסף שלך."
  },
  {
    id: 21,
    number: "2️⃣1️⃣",
    title: "קרן השתלמות",
    category: "pension",
    content: "חיסכון לטווח בינוני (6 שנים), פטור ממס רווחי הון. כלי חזק לעצמאים ולשכירים, המשלב נזילות ותכנון מס.",
    bullTip: "קרן השתלמות זו המתנה הכי שווה שקיבלת מהמדינה – וחבל לבזבז אותה על טלוויזיה בסוף שנה שישית.",
    practicalTip: "בעמוד הקרן שלך תוכל לראות את אחוז המניות, רמת הסיכון והתשואה בפועל. תבדוק אם אתה משקיע אותה כמו שצריך – או רק מחזיק אותה."
  },
  {
    id: 22,
    number: "2️⃣2️⃣",
    title: "קופת גמל להשקעה",
    category: "pension",
    content: "חיסכון גמיש שניתן למשוך בכל עת (עם מס רווחי הון) או להפוך לקצבה פטורה ממס בפרישה. מתאים למי שרוצה גמישות.",
    bullTip: "גמ\"ל להשקעה זה כמו תוכנית חיסכון עם סופר-כוחות. אבל רק אם תדע להשתמש בה נכון – ולא סתם להשאיר אותה במסלול כללי.",
    practicalTip: "בדוק בעמוד המוצר שלך את רמת החשיפה למניות ולחו\"ל – וראה אם אתה מנצל את הפוטנציאל של הכלי הזה כמו שצריך."
  },
  {
    id: 23,
    number: "2️⃣3️⃣",
    title: "קופת גמל רגילה",
    category: "pension",
    content: "חיסכון לטווח ארוך המיועד לפנסיה, עם נזילות מוגבלת עד גיל פרישה או תנאים מיוחדים. כולל רכיבי תגמולים ופיצויים.",
    bullTip: "גמל זה לא מגירה – זה מנוע. אם הוא מושקע במסלול לא מתאים, אתה מפסיד בכל שנה.",
    practicalTip: "כנס לעמוד הקופה שלך ותראה את הרכב ההשקעות ודמי הניהול בפועל. אל תסמוך על ההנחות שקיבלת לפני 10 שנים – תבדוק מה קורה עכשיו."
  },
  {
    id: 24,
    number: "2️⃣4️⃣",
    title: "מוצר קצבתי לעומת הוני",
    category: "pension",
    content: "מוצר קצבתי מייצר קצבה חודשית בפרישה, מוצר הוני מאפשר משיכת סכום חד-פעמי. שילוב נכון תלוי בצרכים שלך בפרישה.",
    bullTip: "אם הכול הוני – תישאר בלי קצבה. אם הכול קצבתי – לא תוכל לעזור לילדים. חכם משלב.",
    practicalTip: "בדוק איזה מוצרים בתיק שלך הם קצבתיים ואילו הוניים. אנחנו מציגים את זה לך בצורה מסודרת – כדי שתוכל לתכנן נכון."
  },
{
  id: 25,
  number: "2️⃣5️⃣",
  title: "ניוד כספים",
  category: "planning",
  content: "מיום שחוק הניוד (2009) עבר בכנסת, אזרחי ישראל קיבלו את הזכות לשמור על ניידות מלאה של החסכונות הפנסיוניים שלהם – במעבר בין מסלולים ובין גופים. הניוד מתבצע ללא אירוע מס, ללא עמלות מכירה וקנייה, ותוך שמירה מלאה על כל מרכיבי הקופה: הוותק, הפטור ממס על רווחים, והזכויות הנוספות שנצברו.\n\n⚠️ החרגה: הזכות לניוד עם שמירת תנאי הקופה אינה כוללת ביטוחי מנהלים עם מקדם קצבה מובטח.",
  bullTip: "חוק הניוד שינה את כללי המשחק: החוסך הישראלי כבר לא כבול לגוף אחד. היום, כל אחד יכול לקחת אחריות על עתידו הפנסיוני, ולבחור בכל רגע נתון את הגוף שינהל את כספיו בצורה המקצועית והמשתלמת ביותר עבורו.",
  practicalTip: "נצל את זכות הניוד בחכמה: בדוק תשואות לאורך זמן, השווה דמי ניהול ושירות, ואל תישאר במקום רק מכוח ההרגל. מצד שני – אם אתה כבר נמצא אצל סוס מנצח, אל תמהר לשנות אותו כמובן."
},
  {
    id: 26,
    number: "2️⃣6️⃣",
    title: "ריבית דריבית",
    category: "investment",
    content: "הרווח שמופק מהרווחים הקודמים, שגורם לחיסכון לצמוח בקצב מואץ לאורך זמן. המפתח לצמיחה בטווח ארוך.",
    bullTip: "מי שלא מושקע – לא רק מפסיד את הריבית. הוא מפסיד גם את הריבית של הריבית. והזמן – לא חוזר.",
    practicalTip: "תשקול להשאיר את הקרן שלך עובדת – אפילו אחרי שחלפו 6 שנים. ככל שהכסף מושקע יותר זמן – האפקט מצטבר."
  },
  {
    id: 27,
    number: "2️⃣7️⃣",
    title: "מוטבים בפנסיה",
    category: "pension",
    content: "האנשים שיקבלו את כספי הפנסיה שלך במקרה פטירה. אם לא הגדרת מוטבים, הכסף יחולק לפי חוק, לאו דווקא לפי רצונך.",
    bullTip: "אם לא כתבת – יכתבו במקומך. ולא תמיד הם יכתבו מה שרצית.",
    practicalTip: "תבדוק אם יש לך מוטבים מוגדרים בתכנית שלך. זה עניין של רגע – ויכול לעשות את כל ההבדל בעת צרה."
  },
  {
    id: 28,
    number: "2️⃣8️⃣",
    title: "תשואה נומינלית לעומת ריאלית",
    category: "investment",
    content: "תשואה נומינלית היא הרווח במספרים, תשואה ריאלית היא הרווח אחרי ניכוי האינפלציה. רק הריאלית מראה כמה באמת הרווחת.",
    bullTip: "10% תשואה עם אינפלציה של 12% זה הפסד בתחפושת. תחשוב ריאלי – או תתפלא לאן נעלם הכסף.",
   practicalTip: "אצלנו תראה את התשואה הנומינלית. כדי להבין כמה היא שווה באמת – תבדוק מה היה המדד באותה תקופה."
  },
  {
    id: 29,
    number: "2️⃣9️⃣",
    title: "הפקדה חודשית קבועה",
    category: "planning",
    content: "מה שבאמת מצמיח את החיסכון - זו לא רק התשואה, אלא גם כמה אתה מפקיד כל חודש. ההתמדה מנצחת הכול.",
    bullTip: "תשואה של 8% בלי הפקדות זה כמו לצעוד במסלול הליכון. אתה זז - אבל לא מתקדם.",
    practicalTip: "אם אתה עצמאי - תכנן הפקדה חודשית קבועה, לא רק סוף שנה. ואם אתה שכיר - תוודא שהמעסיק לא מפספס תשלומים."
  },
{
  id: 30,
  number: "3️⃣0️⃣",
  title: "דוח מצב קיים",
  category: "planning",
  content: "תמונה כוללת של כל החסכונות הפנסיוניים שלך - לפי סוג מוצר, גוף מנהל, יתרות, דמי ניהול והפקדות.",
  bullTip: "בלי דו\"ח מסודר – אתה פשוט מנחש. וכשמדובר בחיסכון הפנסיוני שלך, ניחוש זו לא אסטרטגיה. דו\"ח מצב קיים הוא כלי קריטי שנותן לך ראייה רחבה על כל מה שיש לך: כמה חסכת, כמה הופקד, כמה נגבה בדמי ניהול, ומה הביצועים עד כה. ככה אתה באמת יודע אם הכסף שלך עובד בשבילך – או שאתה עובד קשה והוא מדשדש מאחור.",
  practicalTip: "דו\"ח מצב קיים מאפשר לך לבחון את התמונה המלאה: כל ההפקדות, דמי הניהול, והביצועים – במבט אחד ברור. ההמלצה שלי: עשה לפחות בדיקה אחת לשנה על כלל הנכסים הפנסיוניים שלך, ובדוק אם קיימת התאמה נכונה למצבך האישי כיום ולתנאי השוק המשתנים. זה מה שמבדיל בין חוסך שמתקדם לבין אחד שמופתע ברגע האמת."
},
  {
    id: 31,
    number: "3️⃣1️⃣",
    title: "חיסכון לטווח ארוך",
    category: "planning",
    content: "כל חיסכון שלא נועד למטרה מיידית - אלא לעוד עשור או שניים. דורש סבלנות, התאמת מסלול, והתעלמות מרעשים בטווח הקצר.",
    bullTip: "תיק של טווח ארוך לא מודדים לפי מה קרה אתמול - אלא לפי מה שיישאר איתך בעוד 20 שנה.",
    practicalTip: "כנס לעמוד המוצר שלך ובדוק איך הוא התנהג לאורך 5 שנים לפחות - לא רק השנה. זמן הוא פילטר מצוין לרעש שוק."
  },
  {
    id: 32,
    number: "3️⃣2️⃣",
    title: "חיסכון נזיל",
    category: "planning",
    content: "חיסכון שניתן למשיכה בכל עת - כמו קרן השתלמות אחרי 6 שנים או גמל להשקעה. חשוב לשמור חלק מהחיסכון שלך נגיש - לשעת חירום או להזדמנויות.",
    bullTip: "בלי נזילות - אתה שבוי של החיים. גם חיסכון צריך אוויר.",
    practicalTip: "בדוק כמה מהתיק שלך נזיל בפועל. לעיתים כדאי לשלב מוצר נזיל כמו גמל להשקעה או קרן השתלמות כדי לא להיתקע."
  },
  {
    id: 33,
    number: "3️⃣3️⃣",
    title: "שוק שורי",
    category: "economics",
    content: "מצב בשוק ההון שבו המחירים במגמת עלייה מתמשכת - לרוב בליווי אופטימיות, עלייה בהשקעות ואמון כללי בצמיחה.",
    bullTip: "כשכולם מרוויחים - זה הזמן להיות חכם, לא יהיר. שוק עולה הוא גם שוק מתעתע.",
    practicalTip: "במהלך שוק שורי - אל תתפתה לשנות מסלול בגלל FOMO. תבדוק אם המסלול שלך באמת מתאים לטווח שלך - ואל תנסה לתזמן את השוק."
  },
  {
    id: 34,
    number: "3️⃣4️⃣",
    title: "שוק דובי",
    category: "economics",
    content: "מצב של ירידות מתמשכות בשוק - פחד, בריחה ממניות, ולעיתים פאניקה. מי שלא מוכן - עלול למכור דווקא בשפל.",
    bullTip: "שוק דובי לא נושך - הוא בוחן. הוא בודק אם אתה משקיע או סתם מהמר.",
    practicalTip: "אל תשפוט את המסלול שלך לפי חודש של ירידות. תראה איך הוא התנהג בתקופות דומות בעבר - ומה קרה אחרי."
  },
  {
    id: 35,
    number: "3️⃣5️⃣",
    title: "תחזית מול ביצוע בפועל",
    category: "investment",
    content: "תחזיות הן השערות - ביצועים הם עובדות. מי שבוחר מסלול לפי 'מה שיהיה' - עלול להתאכזב.",
    bullTip: "לא מתכננים פנסיה לפי דיבורים. תכנן לפי נתונים - לא לפי תקווה.",
    practicalTip: "במערכת שלנו תראה מה המסלול באמת עשה - לא מה שהוא אמור לעשות. בדוק לאורך זמן - לא רק שנה אחת."
  },
  {
    id: 36,
    number: "3️⃣6️⃣",
    title: "אגרות חוב ממשלתיות",
    category: "investment",
    content: "הלוואות שהמדינה לוקחת מהציבור. נחשבות סולידיות יחסית, אך לא חפות מסיכון - תלוי בסוג האג\"ח (שקלית, צמודה וכו').",
    bullTip: "אג\"ח ממשלתי זה לא תעודת ביטוח. גם למדינה יש אינפלציה - וגם שם ערך הכסף נשחק.",
    practicalTip: "בעמוד המוצר שלך תוכל לראות את שיעור ההשקעה באג\"ח - אבל לא את האג\"ח הספציפי. אם אתה במסלול סולידי - תוודא שהוא באמת כזה."
  },
  {
    id: 37,
    number: "3️⃣7️⃣",
    title: "אחוז חשיפה לחו\"ל",
    category: "investment",
    content: "כמה מההשקעה שלך מופנית לניירות ערך מחוץ לישראל. לרוב - מניות ואג\"ח בארה\"ב, אירופה ואסיה.",
    bullTip: "מי שנשאר רק בישראל - מפספס את רוב הצמיחה בעולם. אבל גם מי שכל כולו בחו\"ל - מסתכן בלי להבין את המטבע.",
    practicalTip: "בדוק את אחוז החשיפה לחו\"ל במסלול שלך. אם אתה צעיר - ייתכן שכדאי לך יותר גלובליות. אם קרוב לפנסיה - איזון חשוב יותר."
  },
  {
    id: 38,
    number: "3️⃣8️⃣",
    title: "דמי ניהול מצבירה",
    category: "fees",
    content: "אחוז שמנוכה מהכסף שנצבר בקופה. לרוב מדובר על 0.2%-0.9%, אך הפערים בין גופים משמעותיים - במיוחד בפנסיה.",
    bullTip: "אל תשלם על כסף שכבר הרווחת - בלי לדעת מה אתה מקבל בתמורה.",
    practicalTip: "אנחנו מציגים לך את דמי הניהול בפועל, לא רק את המקסימום. תבדוק - אתה עשוי לגלות הפתעות בחשבון."
  },
  {
    id: 39,
    number: "3️⃣9️⃣",
    title: "דמי ניהול מהפקדה",
    category: "fees",
    content: "אחוז שמנוכה מכל הפקדה חודשית חדשה. משפיע מאוד בתחילת הדרך, במיוחד כשאין הרבה צבירה.",
    bullTip: "5% דמי ניהול מהפקדה זה כמו לעבוד בלי לקבל משכורת על ינואר. כל חודש.",
    practicalTip: "במוצרים שלך תוכל לראות את האחוז שנגבה מהפקדה. לפעמים קל להפחית אותו - רק אם בודקים ומבקשים."
  },
{
  id: 40,
  number: "4️⃣0️⃣",
  title: "שווי מצטבר לחיסכון",
  category: "planning",
  content: "הסכום הכולל שנצבר בכל המוצרים הפנסיוניים שלך - כולל גמל, השתלמות, פנסיה וביטוחי מנהלים.",
  bullTip: "זה המספר שיקבע כמה תהיה עצמאי בגיל פרישה - או תלוי בקצבאות.",
  practicalTip: "כיום ניתן להוציא סטטוס פנסיוני עדכני מהמסלקה הפנסיונית של משרד האוצר. הדוח הזה מאגד את כלל הנכסים הפנסיוניים בקטגוריה של קרנות פנסיה, ביטוחי מנהלים, קופות גמל וקרנות השתלמות. תעקבו באופן מובהק על הנכסים הפנסיוניים שלכם כי זה העתיד שלכם, כך לא תופתעו בגיל הפרישה."
},
  {
    id: 41,
    number: "4️⃣1️⃣",
    title: "מסלול השקעה כללי",
    category: "investment",
    content: "מסלול ברירת מחדל ברוב המוצרים - עם פיזור רחב בין מניות, אג\"ח, חו\"ל וישראל. יכול להכיל עד 50% או 60% מניות, תלוי בגוף ובמוצר.",
    bullTip: "כללי נשמע רגוע - אבל בפועל זה לפעמים כמו לנהוג 140 קמ\"ש בלי לבדוק מי הנהג.",
    practicalTip: "בדוק את אחוז המניות והחשיפה הגלובלית בעמוד המסלול שלך. כללי הוא שם - אבל הסיכון משתנה בין גופים."
  },
{
  id: 42,
  number: "4️⃣2️⃣",
  title: "מסלול מנייתי",
  category: "investment",
  content: "מסלול שבו 100% מהכסף מושקע במניות – בישראל, בחו\"ל, או בשילוב בין השניים. בפועל, בחלק מהגופים נשמרת לרוב עד 10% החזקה במזומן, לצורך ניהול נזילות ולקיחת הזדמנויות בשוק. טווח ארוך? מצוין. אבל חובה להבין את התנודתיות.",
  bullTip: "במסלול מנייתי אין בלמים – רק מניות. זהו מסלול שמתאים למשקיעים שמבינים את עוצמת הסיכון לצד פוטנציאל התשואה. התנודתיות חדה, הדרך לא תמיד פשוטה, אך בטווח הארוך היא עשויה להניב את הפירות הגבוהים ביותר.",
  practicalTip: "במסלול זה ההבדלים בין הגופים המנהלים עלולים להיות מהותיים. לכן חשוב במיוחד לבחון בקפידה את ביצועי התשואה לאורך זמן, ולהבין מי מהגופים הצליח להוביל בהתמדה – ומי עמד מאחור."
},
  {
    id: 43,
    number: "4️⃣3️⃣",
    title: "מסלול סולידי",
    category: "investment",
    content: "מסלול שנמנע ממניות - עם השקעה בעיקר באג\"ח סולידי. מתאים למי שקרוב לגיל פרישה או לא סובל סיכון.",
    bullTip: "אפס מניות זה גם אפס תנודתיות - וגם לפעמים אפס תשואה. בטווח ארוך, סולידי זה לא תמיד סולידי.",
    practicalTip: "בדוק את הביצועים של המסלול הסולידי שלך - ייתכן שהוא שמר על השקט, אבל גם דרס את הצמיחה."
  },
  {
    id: 44,
    number: "4️⃣4️⃣",
    title: "קצבה חודשית בפנסיה",
    category: "pension",
    content: "הסכום שתקבל כל חודש בפרישה - מתוך קרן הפנסיה או ביטוח המנהלים. גובה הקצבה מושפע מההפקדות, דמי הניהול, גיל הפרישה והמקדם.",
    bullTip: "הקצבה שלך בעתיד היא תוצאה של ההחלטות שלך היום. אז תחליט חכם - ואל תתפלא בגיל 67.",
    practicalTip: "אנחנו מציגים לך את המוצרים הקצביים בתיק - ואת הפקדותיך אליהם. תעקוב באופן רציף - כדי שלא תגלה את הסכום בדיעבד."
  },
  {
    id: 45,
    number: "4️⃣5️⃣",
    title: "מקדם המרה",
    category: "pension",
    content: "המספר שקובע בכמה כסף תצטרך כדי לקבל קצבה חודשית. לדוגמה: מקדם 200 אומר שצריך 200,000 ₪ כדי לקבל 1,000 ₪ קצבה חודשית.",
    bullTip: "אתה חוסך 40 שנה - ואז מקבל את הקצבה לפי מקדם שאתה לא מכיר. תכיר. זה משנה הכול.",
    practicalTip: "בקרנות פנסיה - המקדם משתנה. בביטוחי מנהלים מסוימים - הוא מובטח. תבדוק אצלך איזה סוג יש - ותכנן בהתאם."
  },
  {
    id: 46,
    number: "4️⃣6️⃣",
    title: "הטבת מס על הפקדות",
    category: "fees",
    content: "על הפקדות לפנסיה וגמל תקבל הטבות מס משמעותיות - ניכוי וזיכוי. במיוחד לעצמאים זו הטבה שאסור לפספס.",
    bullTip: "להשאיר כסף על הרצפה - זו לא אסטרטגיית חיסכון. זה פשוט עצלנות יקרה.",
    practicalTip: "בדוק אם ניצלת את תקרת ההפקדה השנתית שלך. עצמאי? אל תחכה לסוף שנה."
  },
 {
  id: 47,
  number: "4️⃣7️⃣",
  title: "קופת גמל לתגמולים",
  category: "pension",
  content: "הצורה הנפוצה של חיסכון לפנסיה בשוק הפרטי. כוללת רכיב תגמולים ורכיב פיצויים. לרוב מגיעה דרך מעסיק או כעצמאי.",
  bullTip: "אם יש לך יותר מקופה אחת - זה לא יתרון, זה בלגן. תעשה סדר.",
  practicalTip: "במערכת שלנו תוכל לראות כל קופה בצורה ברורה – לפי מספר, מסלול, יתרה ודמי ניהול. חשוב לדעת: קופות הגמל, בדומה לקרנות ההשתלמות, כוללות לא פעם רכיבים הפטורים ממס על רווחים – יתרון משמעותי שמצטבר לאורך זמן. זו עוד סיבה קריטית למה כדאי תמיד לדעת בדיוק היכן הכסף שלך מושקע, ולא להשאיר את העתיד הפיננסי שלך בערפל."
},
  {
    id: 48,
    number: "4️⃣8️⃣",
    title: "גירעון אקטוארי",
    category: "pension",
    content: "מצב שבו לקרן הפנסיה אין מספיק כסף למלא את התחייבויותיה העתידיות. עלול להוביל להפחתה בזכויות - או להעלאת הפקדות.",
    bullTip: "גירעון זה לא רק בעיה של הקרן - זה בעיה שלך. כי בעת פדיון - כבר לא תוכל לשנות גוף.",
    practicalTip: "בדוק אם הקרן שלך באיזון אקטוארי. אפשר לראות את זה באתר הקרן או בדוחות השנתיים. קרן בגרעון? שווה לעקוב ברצינות."
  },
  {
    id: 49,
    number: "4️⃣9️⃣",
    title: "קופת פיצויים לא משויכת",
    category: "pension",
    content: "פיצויים שהופרשו עבורך - אבל אינם משויכים למוצר פנסיוני פעיל. עלולים להישכח או להתנהל בתנאים גרועים.",
    bullTip: "יש לך פיצויים שלא דיברת עליהם שנים? אולי גם הגוף שמנהל אותם שכח מהם.",
    practicalTip: "תבדוק אם יש לך קופות כאלה בדוח. תבדוק מסלול, דמי ניהול, ואם שווה למזג או לנתב אותן למסלול פעיל."
  },
  {
    id: 50,
    number: "5️⃣0️⃣",
    title: "השוואת מסלולים",
    category: "planning",
    content: "בדיקה של ביצועים, דמי ניהול והרכב השקעות בין מסלולים מקבילים. חובה לכל חוסך - במיוחד לפני שינוי מסלול או ניוד.",
    bullTip: "אל תבחר מסלול לפי שם, ולא לפי המלצה של שכן. תבחר לפי נתונים - ולא לפי אינטואיציה.",
    practicalTip: "אנחנו מציגים את הנתונים המלאים על כל מסלול: תשואות, הרכב השקעות, סיכון ומידע רגולטורי. תיכנס, תשווה - ותחליט חכם."
  },
  {
    id: 51,
    number: "5️⃣1️⃣",
    title: "תשואת יתר (אלפא)",
    category: "investment",
    content: "יכולת מנהל ההשקעות להשיג תשואה גבוהה יותר ממדד הייחוס.",
    bullTip: "אלפא אמיתית זה לא 'פעם אחת פגע' - אלא עקביות שמחזיקה עשורים.",
    practicalTip: "בדוק במסלולים אצלנו מי עקף את המדד לאורך כמה שנים - זה ההבדל בין מזל לניהול."
  },
  {
    id: 52,
    number: "5️⃣2️⃣",
    title: "סטיית תקן",
    category: "investment",
    content: "מדד לתנודתיות – כמה רחוקות התשואות מהממוצע.",
    bullTip: "סטייה גבוהה בלי פרס – זו סתם רכבת הרים בלי יעד.",
    practicalTip: "ראה במסלולים אצלנו את מדדי התנודתיות – ותשווה לרמת הנוחות שלך עם סיכון."
  },
  {
    id: 53,
    number: "5️⃣3️⃣",
    title: "יחס P/E",
    category: "investment",
    content: "יחס בין מחיר מניה לרווח למניה. כלי פופולרי להערכת שווי.",
    bullTip: "P/E גבוה זה לא תמיד בועה – לפעמים זו ציפייה לעתיד.",
    practicalTip: "במסלולים אין מניות ספציפיות – אבל החשיפה לענפים מושפעת מהיחסים האלה."
  },
  {
    id: 54,
    number: "5️⃣4️⃣",
    title: "דיבידנד",
    category: "investment",
    content: "תשלום רווחים של חברה לבעלי המניות. מקור יציב, אך בא על חשבון השקעה בצמיחה.",
    bullTip: "לא כל מי שמחלק – מחייך. לפעמים זו חברה שנגמר לה הכוח.",
    practicalTip: "בדוק אם במסלול שלך יש חשיפה לחברות מחלקות דיבידנד – זה משנה את אופיו."
  },
  {
    id: 55,
    number: "5️⃣5️⃣",
    title: "ESG – השקעות אחראיות",
    category: "investment",
    content: "השקעות המנוהלות לפי סביבה, חברה וממשל תאגידי.",
    bullTip: "מוסריות זה יופי – אבל היא צריכה גם להרוויח.",
    practicalTip: "ראה אם הגוף שלך מציע מסלול ESG תחת משרד האוצר – זה נתון רשמי, לא שיווקי."
  },
  {
    id: 56,
    number: "5️⃣6️⃣",
    title: "יחס חוב־תוצר",
    category: "economics",
    content: "מודד את יכולת המדינה להחזיר חובות.",
    bullTip: "גם מדינות יכולות להסתבך בחובות – תשאל את יוון.",
    practicalTip: "בחשיפת אג\"ח ממשלתיות במסלול שלך – זו בעצם החשיפה לסיכון הזה."
  },
  {
    id: 57,
    number: "5️⃣7️⃣",
    title: "מינוף",
    category: "investment",
    content: "השקעה בכסף שאול להגדלת סיכוי – ולסיכון.",
    bullTip: "מינוף זה כמו אש – חם, אבל שורף מהר.",
    practicalTip: "ברוב המסלולים הפנסיוניים אין מינוף – וזה יתרון גדול."
  },
  {
    id: 58,
    number: "5️⃣8️⃣",
    title: "נזילות שוק",
    category: "investment",
    content: "היכולת למכור נכס בלי 'לשבור' את המחיר.",
    bullTip: "נכס בלי קונים זה לא נכס – זו מלכודת.",
    practicalTip: "בדוק אם יש במסלול שלך נכסים חלופיים – שם הנזילות מוגבלת."
  },
  {
    id: 59,
    number: "5️⃣9️⃣",
    title: "תשואת חסר",
    category: "investment",
    content: "כאשר מנהל ההשקעות לא מגיע אפילו למדד.",
    bullTip: "לשלם דמי ניהול ולקבל פחות מהמדד – זו מתנה יקרה מדי.",
    practicalTip: "השווה תמיד את המסלול שלך למדד – זו האמת הפשוטה."
  },
  {
    id: 60,
    number: "6️⃣0️⃣",
    title: "פרופיל סיכון",
    category: "investment",
    content: "ההתאמה שלך ליכולת לשאת סיכון.",
    bullTip: "אין כזה דבר 'בלי סיכון' – גם מזומן נשחק.",
    practicalTip: "בדוק את אחוז המניות במסלול שלך – ותראה אם זה באמת אתה."
  },
  {
    id: 61,
    number: "6️⃣1️⃣",
    title: "תיק השקעות",
    category: "investment",
    content: "האוסף הכולל של כל ההשקעות שלך – פנסיוניות ואישיות.",
    bullTip: "תיק לא בונים ביום – אלא חיים שלמים.",
    practicalTip: "בדוק אם התיק שלך לא כפול באפיקים דומים – זה חוסך טעויות."
  },
  {
    id: 62,
    number: "6️⃣2️⃣",
    title: "מדד שארפ",
    category: "investment",
    content: "מודד כמה תשואה קיבלת על כל יחידת סיכון.",
    bullTip: "תשואה בלי סיכון זה חלום. סיכון בלי תשואה זה סיוט.",
    practicalTip: "אנחנו מציגים מדד שארפ – תראה אם הסיכון שלך באמת משתלם."
  },
  {
    id: 63,
    number: "6️⃣3️⃣",
    title: "השקעות אלטרנטיביות",
    category: "investment",
    content: "השקעות לא שגרתיות – נדל\"ן פרטי, קרנות גידור, תשתיות.",
    bullTip: "אלטרנטיבי זה לא קסם – לפעמים זה פשוט לא נזיל.",
    practicalTip: "בדוק במסלול שלך את אחוז ההשקעות הלא־סחירות – ותבין את החיסרון."
  },
  {
    id: 64,
    number: "6️⃣4️⃣",
    title: "Value מול Growth",
    category: "investment",
    content: "שתי גישות להשקעה במניות – ערך מול צמיחה.",
    bullTip: "ערך זה יציבות, צמיחה זה חלום – חכם בודק מה יש בפנים.",
    practicalTip: "בחשיפה לענפים אצלנו תוכל לראות איזה סוג דומיננטי במסלול שלך."
  },
  {
    id: 65,
    number: "6️⃣5️⃣",
    title: "סיכון מט\"ח",
    category: "investment",
    content: "ההשפעה של שינויי מטבע על ההשקעה שלך.",
    bullTip: "השקל יכול להפוך רווח יפה להפסד מרגיז.",
    practicalTip: "בדוק כמה אחוז מהתיק שלך בחו\"ל – זה מדד לסיכון מט\"חי."
  },
  {
    id: 66,
    number: "6️⃣6️⃣",
    title: "ריבית חסרת סיכון",
    category: "investment",
    content: "הבסיס להשוואת כל השקעה – לרוב אג\"ח ממשלתי קצר.",
    bullTip: "אין באמת 'חסר סיכון'. יש 'חסר תשואה'.",
    practicalTip: "ראה את מדד שארפ אצלנו – הוא כבר כולל את ריבית הבסיס הזו."
  },
  {
    id: 67,
    number: "6️⃣7️⃣",
    title: "מכפיל הון (P/B)",
    category: "investment",
    content: "יחס שווי שוק להון עצמי של חברה.",
    bullTip: "מכפיל נמוך לא תמיד הזדמנות – לפעמים זו אזהרה.",
    practicalTip: "בחירת מסלול עם חשיפה לענפים שונים מאזנת גם את הסיכון הזה."
  },
  {
    id: 68,
    number: "6️⃣8️⃣",
    title: "תשואה לפדיון",
    category: "investment",
    content: "התשואה הכוללת באג\"ח אם נשמר עד הסוף.",
    bullTip: "אג\"ח עד הסוף זה כמו חתונה – לא תמיד כדאי להישאר עד הקצה.",
    practicalTip: "בדוק במסלולים עם אג\"ח מהי התשואה המשוערת – היא קובעת הכול."
  },
  {
    id: 69,
    number: "6️⃣9️⃣",
    title: "תקרת הפקדה",
    category: "fees",
    content: "המגבלה המקסימלית להפקדה מוכרת במס.",
    bullTip: "מי שלא מנצל תקרה – משאיר מתנה למדינה.",
    practicalTip: "בדוק כמה ניצלת השנה – זה מופיע בדוחות שלך."
  },
  {
    id: 70,
    number: "7️⃣0️⃣",
    title: "חוסן פיננסי",
    category: "planning",
    content: "היכולת שלך לעמוד במשברים בלי למכור בזול.",
    bullTip: "חוסן זה לא כמה הרווחת – אלא כמה אתה מסוגל לספוג.",
    practicalTip: "תבנה תיק עם פיזור אמיתי – לא עם 'שם יפה'."
  },
  {
    id: 71,
    number: "7️⃣1️⃣",
    title: "אגרות חוב קונצרניות",
    category: "investment",
    content: "אג\"ח שמנפיקות חברות פרטיות.",
    bullTip: "חברה בלי כסף – לא מחזירה אג\"ח, גם אם היא 'מותג'.",
    practicalTip: "בדוק מה אחוז האג\"ח הקונצרני במסלול שלך – זה אומר כמה סיכון לקחת."
  },
  {
    id: 72,
    number: "7️⃣2️⃣",
    title: "סחירות",
    category: "investment",
    content: "עד כמה ניתן למכור נכס במהירות ובשקיפות מחיר.",
    bullTip: "נייר סחיר זה נזיל. נייר לא סחיר – זה כאב ראש.",
    practicalTip: "תראה אצלנו את אחוז הנכסים הלא־סחירים במסלול."
  },
  {
    id: 73,
    number: "7️⃣3️⃣",
    title: "עמלות ניהול השקעות",
    category: "fees",
    content: "עלויות פנימיות של הגוף המנהל – לא תמיד שקופות.",
    bullTip: "דמי ניהול זה לא רק מה שאתה רואה – זה גם מה שמסתתר מתחת.",
    practicalTip: "בדוחות תוכל לראות אם הגוף שלך 'יקר מדי' לעומת התשואה."
  },
  {
    id: 74,
    number: "7️⃣4️⃣",
    title: "תשואה ממוצעת שנתית",
    category: "investment",
    content: "מייצגת את התשואה לאורך זמן, לא רק שנה בודדת.",
    bullTip: "ממוצע מחליק טעויות – אבל לא מוחק אותן.",
    practicalTip: "בדוק 5 שנים אחורה לפחות – זה נותן תמונה אמיתית."
  },
  {
    id: 75,
    number: "7️⃣5️⃣",
    title: "Duration – משך חיים ממוצע",
    category: "investment",
    content: "מדד רגישות אג\"ח לשינויים בריבית.",
    bullTip: "אג\"ח עם Duration גבוה – מתנדנד כמו מפרש בסערה.",
    practicalTip: "בדוק את רגישות האג\"ח במסלול שלך – זה קריטי כשהריביות משתנות."
  },
  {
    id: 76,
    number: "7️⃣6️⃣",
    title: "תעודות סל (ETF)",
    category: "investment",
    content: "מכשיר השקעה שעוקב אחרי מדד.",
    bullTip: "ETF זה פשטות – אבל קיבלת גם את כל הטוב וגם את כל הרע של המדד.",
    practicalTip: "בדוק אם הגוף שלך משתמש ב־ETF כחלק מהניהול. זה יעזור להבין את אופי הניהול."
  },
  {
    id: 77,
    number: "7️⃣7️⃣",
    title: "קרנות מחקות",
    category: "investment",
    content: "קרנות ישראליות שעוקבות אחרי מדדים.",
    bullTip: "קרן מחקה לא מנצחת מדד – היא נצמדת אליו.",
    practicalTip: "בדוק אם במסלול שלך יש רכיב פאסיבי כזה."
  },
  {
    id: 78,
    number: "7️⃣8️⃣",
    title: "השקעות גיאוגרפיות",
    category: "investment",
    content: "פיזור בין מדינות ואזורים.",
    bullTip: "מי שמשקיע רק בבית – מפספס את העולם.",
    practicalTip: "תראה כמה אחוז אצלך בחו\"ל – זה מפתח להבנת החשיפה."
  },
  {
    id: 79,
    number: "7️⃣9️⃣",
    title: "NAV – שווי נכסי נקי",
    category: "investment",
    content: "השווי של נכסי הקרן פחות התחייבויותיה.",
    bullTip: "NAV גבוה זה לא תמיד רווח – זה פשוט חשבון מתמטי.",
    practicalTip: "בדוחות הקרנות זה הנתון המרכזי – תלמד לקרוא אותו."
  },
  {
    id: 80,
    number: "8️⃣0️⃣",
    title: "תשואה שנתית נומינלית",
    category: "investment",
    content: "הרווח המדווח במספרים מוחלטים לשנה.",
    bullTip: "נומינלי זה יפה, אבל אם המדד חזק יותר – זה הפסד בתחפושת.",
    practicalTip: "תמיד בדוק מול האינפלציה כדי לדעת מה נשאר לך באמת."
  },
  {
    id: 81,
    number: "8️⃣1️⃣",
    title: "Cost Average – השקעה קבועה",
    category: "investment",
    content: "השקעה בסכום קבוע כל חודש.",
    bullTip: "השוק לא מתזמן אותך – אתה מתזמן אותו בהתמדה.",
    practicalTip: "הפקדה חודשית קבועה זה היתרון הכי גדול שלך מול השוק."
  },
  {
    id: 82,
    number: "8️⃣2️⃣",
    title: "Diversification – גיוון",
    category: "investment",
    content: "פיזור השקעות בין אפיקים שונים.",
    bullTip: "גיוון הוא חומת ההגנה של משקיע חכם.",
    practicalTip: "בדוק במסלול שלך את אחוזי המניות מול אג\"ח – זה הגיוון שלך."
  },
  {
    id: 83,
    number: "8️⃣3️⃣",
    title: "תשואת דיבידנד",
    category: "investment",
    content: "אחוז התשואה ממניות מחלקות.",
    bullTip: "דיבידנד גבוה מדי? אולי זו נורת אזהרה.",
    practicalTip: "בדוק אם במסלול שלך יש חשיפה לחברות מחלקות דיבידנד."
  },
  {
    id: 84,
    number: "8️⃣4️⃣",
    title: "Beta Adjusted Return",
    category: "investment",
    content: "תשואה מותאמת סיכון.",
    bullTip: "בלי להתחשב בבטא – אי אפשר לדעת אם זה באמת שווה.",
    practicalTip: "אנחנו מציגים מדדים כאלה בחלק מהמסלולים."
  },
  {
    id: 85,
    number: "8️⃣5️⃣",
    title: "תוחלת חיים פיננסית",
    category: "planning",
    content: "כמה זמן הכסף שלך יחזיק בפרישה.",
    bullTip: "הבעיה היא לא לחיות יותר – אלא בלי כסף.",
    practicalTip: "חשב את זה לפי הקצבאות הצפויות מול ההוצאות."
  },
  {
    id: 86,
    number: "8️⃣6️⃣",
    title: "Real Return – תשואה ריאלית",
    category: "investment",
    content: "תשואה לאחר קיזוז אינפלציה.",
    bullTip: "הרווחת פחות מהמדד? הפסדת בשקט.",
    practicalTip: "בדוק תמיד מול מדד המחירים לצרכן."
  },
  {
    id: 87,
    number: "8️⃣7️⃣",
    title: "שוק ראשוני",
    category: "investment",
    content: "שוק בו מונפקים ניירות ערך חדשים.",
    bullTip: "להיות ראשון זה לא תמיד רווחי – לפעמים זה יקר.",
    practicalTip: "בפנסיה פחות רלוונטי – אבל חשוב להבין את העיקרון."
  },
  {
    id: 88,
    number: "8️⃣8️⃣",
    title: "שוק משני",
    category: "investment",
    content: "שוק בו נסחרים ניירות שכבר הונפקו.",
    bullTip: "פה נעשה המשחק האמיתי – לא בהנפקה.",
    practicalTip: "שם מתממשות התשואות שלך בפועל."
  },
  {
    id: 89,
    number: "8️⃣9️⃣",
    title: "Liquidity Ratio – יחס נזילות",
    category: "investment",
    content: "מודד את יכולת פירעון התחייבויות בטווח קצר.",
    bullTip: "חברה בלי נזילות – זה כמו בית בלי מזומן.",
    practicalTip: "לא תראה את זה ישירות במסלולים – אבל זה משפיע על סיכון אג\"ח קונצרני."
  },
  {
    id: 90,
    number: "9️⃣0️⃣",
    title: "Margin Call",
    category: "investment",
    content: "דרישה להוסיף בטחונות בהשקעות ממונפות.",
    bullTip: "Margin Call זה הטלפון שאתה לא רוצה לקבל.",
    practicalTip: "בפנסיה זה לא קיים – וזה לטובתך."
  },
  {
    id: 91,
    number: "9️⃣1️⃣",
    title: "Rebalancing – איזון מחדש",
    category: "investment",
    content: "התאמת התיק חזרה ליעדים המקוריים.",
    bullTip: "תיק לא מאוזן זה כמו רכב בלי יישור גלגלים.",
    practicalTip: "הגופים המנהלים מבצעים איזון כזה – תבדוק אם זה עובד לטובתך."
  },
  {
    id: 92,
    number: "9️⃣2️⃣",
    title: "IRR – שיעור תשואה פנימי",
    category: "investment",
    content: "מודד את שיעור התשואה הממוצע להשקעה לאורך זמן.",
    bullTip: "IRR זה פחות מספר – יותר סיפור של תזרים.",
    practicalTip: "שימושי בעיקר בנדל\"ן והשקעות פרטיות – פחות בקרנות."
  },
  {
    id: 93,
    number: "9️⃣3️⃣",
    title: "תזמון שוק (Market Timing)",
    category: "investment",
    content: "ניסיון לתזמן כניסה ויציאה מהשוק – לקנות בזול ולמכור ביוקר. בפועל, רוב המשקיעים נכשלים בזה לאורך זמן.",
    bullTip: "לתזמן שוק זה הימור – להשקיע לאורך זמן זה אסטרטגיה.",
    practicalTip: "במקום לנסות 'לתפוס את היום הנכון', עדיף להפקיד באופן קבוע במסלול מתאים לטווח שלך. אצלנו תוכל לראות מהו המסלול עם הפיזור והחשיפה שמתאימים לך – ולתת לזמן לעבוד עבורך."
  },
  {
    id: 94,
    number: "9️⃣4️⃣",
    title: "Diversification Bias – הטיית הפיזור",
    category: "investment",
    content: "משקיעים נוטים לחשוב שיותר פיזור = פחות סיכון. אבל פיזור יתר יוצר חיקוי עיוור של המדד – בלי יכולת לייצר ערך אמיתי.",
    bullTip: "מי שמפזר בלי לחשוב – פשוט קונה את כל הסופר. בסוף אתה נשאר עם עגלה מלאה… וחוויה ריקה.",
    practicalTip: "בדוק אם המסלול שלך באמת מפזר חכם – או פשוט עוקב אחרי כולם."
  },
  {
    id: 95,
    number: "9️⃣5️⃣",
    title: "Drawdown – ירידה מצטברת",
    category: "investment",
    content: "מדד שמראה כמה רחוק ירד תיק ההשקעות מהשיא שלו עד לתחתית. זה לא מספר – זה מבחן העצבים של המשקיע.",
    bullTip: "רווח זה קל. עמידה בירידה של 20% זה המקום שבו מתגלים המשקיעים האמיתיים.",
    practicalTip: "בדוק במסלול שלך מה היה ה־Drawdown ההיסטורי – זה רמז למה שיכול לקרות במפולת הבאה."
  },
  {
    id: 96,
    number: "9️⃣6️⃣",
    title: "Risk Premium – פרמיית סיכון",
    category: "investment",
    content: "התשואה הנוספת שמשקיע דורש כדי לקחת סיכון מעבר ל'חסר סיכון'.",
    bullTip: "אין ארוחות חינם – כל תשואה עודפת היא בעצם שכר על הפחד שלך.",
    practicalTip: "בדוק במסלול שלך אם באמת יש לך פרמיה עודפת מול מדד חסר סיכון – או שאתה עובד בחינם."
  },
  {
    id: 97,
    number: "9️⃣7️⃣",
    title: "Alpha Decay – שחיקת אלפא",
    category: "investment",
    content: "גם מנהלים מעולים שמצליחים להביס את השוק – מתקשים לשמור על זה לאורך שנים. האלפא נשחקת כשהשוק לומד את הטריקים.",
    bullTip: "מי שחי על אלפא רגעית – מתעורר יום אחד וגלה שהוא קנה 'קסם חד־פעמי'.",
    practicalTip: "בדוק את עקביות התשואות במסלול שלך לאורך 5–10 שנים – לא רק שנה אחת נוצצת."
  },
  {
    id: 98,
    number: "9️⃣8️⃣",
    title: "Sharpe Ratio – מדד שארפ",
    category: "investment",
    content: "מודד כמה עודף תשואה קיבלת על כל יחידת סיכון, ביחס לריבית חסרת סיכון.",
    bullTip: "שוק עולה הופך את כולם לגאונים. מדד שארפ הוא זה שמפריד בין מזל לניהול.",
    practicalTip: "אנחנו מציגים לך את מדד השארפ במסלולים – זה כלי שמראה אם הסיכון שלך היה באמת חכם."
  },
  {
    id: 99,
    number: "9️⃣9️⃣",
    title: "Tail Risk – סיכון זנב",
    category: "investment",
    content: "הסיכוי לאירוע נדיר אבל הרסני – כמו קריסה פיננסית, מגפה או מלחמה.",
    bullTip: "הבעיות האמיתיות לא מגיעות מהיומיום – הן באות מהזנב. מי שלא מוכן – נטרף.",
    practicalTip: "בדוק אם המסלול שלך מחזיק רכיבים מגִנים (כמו אג\"ח קצרות או מזומן) – כדי שלא תיפול כשתגיע 'ברבור שחור'."
  },
  {
    id: 100,
    number: "1️⃣0️⃣0️⃣",
    title: "FIRE – Financial Independence, Retire Early",
    category: "planning",
    content: "תנועה עולמית לחופש כלכלי ופרישה מוקדמת. רעיון יפה – אבל דורש משמעת השקעתית ברזל.",
    bullTip: "לפרוש בגיל 40 זה חלום. אבל אם לא השקעת חכם – זה גם הסיוט שבו אתה חוזר לעבוד בגיל 50.",
    practicalTip: "השתמש בכלי כמו קרן השתלמות וגמל להשקעה – הם נותנים לך יתרון מס אדיר במסע ל־FIRE."
  }
];
  const categories = [
    { id: 'all', name: 'כל הקטגוריות', icon: BookOpen },
    { id: 'investment', name: 'השקעות', icon: TrendingUp },
    { id: 'pension', name: 'פנסיה וגמל', icon: PieChart },
    { id: 'fees', name: 'עלויות וניהול', icon: DollarSign },
    { id: 'economics', name: 'כלכלה', icon: Calculator },
    { id: 'planning', name: 'תכנון פיננסי', icon: Filter }
  ];

  const filteredTerms = terms.filter(term => {
    const matchesSearch = term.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         term.content.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || term.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const toggleExpanded = (termId) => {
    setExpandedTerm(expandedTerm === termId ? null : termId);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 p-4" dir="rtl">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 mb-8 border border-gray-100">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <BookOpen className="h-12 w-12 text-blue-600" />
              <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                מילון מונחים פיננסיים ופנסיונים
              </h1>
            </div>
            <p className="text-xl text-gray-600 mb-6">
              המדריך השלם לעולם ההשקעות, הפנסיה והחיסכון
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
              <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-medium">
                🐂 טיפים של השור
              </span>
              <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full font-medium">
                💡 טיפים פרקטיים
              </span>
            </div>
          </div>
        </div>
        {/* Search and Filter Bar */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-8 border border-gray-100">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search Input */}
            <div className="flex-1 relative">
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                ref={searchRef}
                type="text"
                placeholder="חפש מונח..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-10 pl-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
              />
            </div>
            {/* Category Filter */}
            <div className="flex gap-2 overflow-x-auto lg:overflow-visible">
              {categories.map(category => {
                const IconComponent = category.icon;
                return (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`flex items-center gap-2 px-4 py-3 rounded-lg font-medium transition-all whitespace-nowrap ${
                      selectedCategory === category.id
                        ? 'bg-blue-600 text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <IconComponent className="h-4 w-4" />
                    {category.name}
                  </button>
                );
              })}
            </div>
          </div>
          {/* Results Counter */}
          <div className="mt-4 text-sm text-gray-600 flex items-center gap-2">
            <Filter className="h-4 w-4" />
            נמצאו {filteredTerms.length} מונחים
          </div>
        </div>
        {/* Terms Grid */}
        <div className="space-y-6">
          {filteredTerms.map((term) => {
            const isExpanded = expandedTerm === term.id;
            return (
              <div key={term.id} className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                {/* Term Header */}
                <div
                  className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => toggleExpanded(term.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="text-2xl">{term.number}</div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-800 mb-1">
                          {term.title}
                        </h3>
                        <span className="text-sm text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                          {categories.find(cat => cat.id === term.category)?.name}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isExpanded ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      {isExpanded ? <ChevronUp className="h-6 w-6" /> : <ChevronDown className="h-6 w-6" />}
                    </div>
                  </div>
                </div>
                {/* Expanded Content */}
                {isExpanded && (
                  <div className="px-6 pb-6 border-t border-gray-100">
                    <div className="space-y-6">
                      {/* Main Content */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                          <BookOpen className="h-4 w-4" />
                          הגדרה
                        </h4>
                        <p className="text-gray-700 leading-relaxed">
                          {term.content}
                        </p>
                      </div>
                      {/* Bull Tip */}
                      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4">
                        <h4 className="font-bold text-amber-800 mb-2 flex items-center gap-2">
                        הטיפ של הטיפ של השור מגמלנט AI 🐂
                        </h4>
                        <p className="text-amber-700 leading-relaxed font-medium">
                          {term.bullTip}
                        </p>
                      </div>
                      {/* Practical Tip */}
                      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
                        <h4 className="font-bold text-green-800 mb-2 flex items-center gap-2">
                          💡 טיפ פרקטי מהשור של גמלנט AI
                        </h4>
                        <p className="text-green-700 leading-relaxed">
                          {term.practicalTip}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {/* No Results */}
        {filteredTerms.length === 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <Search className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-600 mb-2">לא נמצאו תוצאות</h3>
            <p className="text-gray-500">
              נסה לשנות את מונח החיפוש או הקטגוריה
            </p>
          </div>
        )}
        {/* Footer */}
        <div className="mt-12 text-center bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <div className="flex items-center justify-center gap-2 mb-4">
            <TrendingUp className="h-6 w-6 text-blue-600" />
            <span className="text-xl font-bold text-gray-800">גמל AI</span>
          </div>
          <p className="text-gray-600 mb-4">
            המקור המהימן שלך לכל מה שקשור לחיסכון, השקעות ופנסיה
          </p>
          <div className="text-sm text-gray-500">
            עודכן לאחרונה: {new Date().toLocaleDateString('he-IL')}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialDictionary;